import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { CofreBiometrico } from 'cofre-biometrico';
import { ponteDoAplicativo } from './aplicativo';

/**
 * A CONTA DA SALA NESTE APARELHO (02/11/2026, decisão do pesquisador; o lado do aparelho é `desktop/src/contaDaSala.js`).
 *
 * SEM INTERNET, SEMPRE. "O professor logado", para a sala, é a conta guardada no aparelho — e não a sessão do portal:
 *   - entra quando a conta da equipe entra no portal (`lembrarContaDaSala`, chamado por `useAuth`);
 *   - SÓ SAI com o "Sair" de propósito (`esquecerContaDaSala`). A sessão do portal vencida (o `auth:logout` do interceptador)
 *     NÃO a tira: o limite da sala é a credencial dela (30 dias);
 *   - abrir e apagar uma sala pedem o desbloqueio LOCAL: no celular, a biometria ou a senha do aparelho
 *     (`confirmarDono`, o cofre do sistema); no computador, o Touch ID ou o PIN — quem pede é o próprio aplicativo.
 * No computador a conta mora no aplicativo (a ponte, 2.0.21+); no celular, em `@capacitor/preferences` (sobrevive ao
 * logout forçado, que só apaga o `localStorage` da sessão).
 */
export interface ContaDaSala { id: number; nome: string }
const CHAVE = 'sala:conta';
const EQUIPE = ['TEACHER', 'SCHOOL_STAFF', 'ADMIN'];

export async function lembrarContaDaSala(user: { id: number | string; name?: string; role?: string } | null | undefined): Promise<void> {
  if (!user || !EQUIPE.includes(String(user.role))) return;
  const conta = { id: Number(user.id), nome: String(user.name ?? '') };
  const ponte = ponteDoAplicativo();
  if (ponte?.contaDaSala) { await ponte.contaDaSala(conta).catch(() => undefined); return; }
  if (Capacitor.isNativePlatform()) await Preferences.set({ key: CHAVE, value: JSON.stringify(conta) }).catch(() => undefined);
}

/** O "Sair" de propósito: a conta sai do aparelho, e as salas dela ficam travadas (no computador, as nunca abertas saem). */
export async function esquecerContaDaSala(id: number | string | undefined): Promise<void> {
  const ponte = ponteDoAplicativo();
  if (ponte?.sairDaConta) { await ponte.sairDaConta(id == null ? null : Number(id)).catch(() => undefined); return; }
  if (Capacitor.isNativePlatform()) await Preferences.remove({ key: CHAVE }).catch(() => undefined);
}

/** A conta guardada no celular (sem chamada nenhuma ao portal), ou nula. */
export async function contaDaSalaNoCelular(): Promise<ContaDaSala | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try { const { value } = await Preferences.get({ key: CHAVE }); const c = value ? JSON.parse(value) : null; return c && Number(c.id) > 0 ? { id: Number(c.id), nome: String(c.nome ?? '') } : null; } catch { return null; }
}

/** O celular tem bloqueio de tela? Nulo = o app não sabe responder (o build de antes do 02/11). */
export async function celularProtegido(): Promise<boolean | null> {
  try { return (await CofreBiometrico.protegido()).protegido; } catch { return null; }
}

/**
 * O desbloqueio local do dono, no celular: Face ID, digital ou a senha do aparelho. Lança com a frase (cancelou, sem
 * bloqueio, app antigo). Nada vai ao portal.
 */
export async function confirmarDono(motivo: string): Promise<void> {
  try { await CofreBiometrico.confirmarDono({ titulo: motivo }); }
  catch (e) {
    const code = (e as { code?: string }).code;
    if (code === 'CANCELADO') throw Object.assign(new Error('Cancelado.'), { cancelado: true });
    if (code === 'SEM_BLOQUEIO') throw new Error('Este aparelho não tem bloqueio de tela (código, PIN ou digital). Ative-o nos ajustes para abrir a sala.');
    if (code === 'UNIMPLEMENTED' || /not implemented/i.test((e as Error)?.message ?? '')) throw new Error('Atualize o aplicativo Portal Simulados para abrir a sala com o desbloqueio do aparelho.');
    throw new Error('Não foi possível confirmar que é você. Tente de novo.');
  }
}
